import React, { createContext, useContext, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { createErrorHandler, handleAsyncOperation } from './shared/errorHandler';

const AuthContext = createContext();

// Auth-specific actions
const authActions = {
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT'
};

// Auth-specific reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case authActions.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };
    case authActions.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    default:
      return state;
  }
};

const initialState = createBaseState({
  user: null,
  isAuthenticated: false
});

export const AuthProvider = ({ children }) => {
  const { state, dispatch, setLoading, setError, clearError } = useBaseContext(initialState, authReducer);
  const [isInitializing, setIsInitializing] = React.useState(true);
  const lastUserDataRef = useRef(null);
  const errorHandler = createErrorHandler('AUTH');

  // User session handler
  const handleUserSession = async (authUser, isTokenRefresh = false) => {
    try {
      if (isTokenRefresh && lastUserDataRef.current && lastUserDataRef.current.id === authUser.id) {
        dispatch({ type: authActions.SET_USER, payload: lastUserDataRef.current });
        return;
      }

      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Database query timeout')), 3000) // Reduced from 5000ms to 3000ms
      );

      const { data: profile, error } = await Promise.race([queryPromise, timeoutPromise]);

      if (error) {
        if (error.message === 'Database query timeout' && lastUserDataRef.current) {
          dispatch({ type: authActions.SET_USER, payload: lastUserDataRef.current });
          return;
        }
        if (error.code === 'PGRST116') {
          createUserFromAuth(authUser);
          return;
        }
        throw error;
      }

      if (profile) {
        const userData = {
          id: profile.id,
          username: profile.username,
          displayName: profile.display_name,
          email: authUser.email,
          avatarUrl: profile.use_discord_avatar !== false
            ? (profile.original_discord_avatar || profile.avatar_url)
            : profile.avatar_url,
          originalDiscordAvatar: profile.original_discord_avatar,
          useDiscordAvatar: profile.use_discord_avatar !== false,
          discordId: profile.discord_id,
          isAdmin: profile.role === 'admin',
          bio: profile.bio,
          platform: profile.platform,
          gamerTag: profile.gamer_tag,
          position: profile.position,
          jerseyNumber: profile.jersey_number,
          countryCode: profile.country_code,
          stats: {
            tournamentsPlayed: profile.tournaments_played || 0,
            tournamentsWon: profile.tournaments_won || 0,
            totalWins: profile.total_wins || 0,
            totalLosses: profile.total_losses || 0,
            eloRating: profile.elo_rating || 1000
          }
        };

        lastUserDataRef.current = userData;
        dispatch({ type: authActions.SET_USER, payload: userData });

        // Auto-sync Discord profile on login (background, don't await)
        if (profile.discord_id && !isTokenRefresh) {
          syncDiscordProfile().catch(error => {
            console.warn('Discord profile auto-sync failed:', error);
            // Don't throw - this shouldn't block login
          });
        }
      } else {
        createUserFromAuth(authUser);
      }
    } catch (error) {
      console.error('[AUTH] Error:', error.message);
      if (lastUserDataRef.current && lastUserDataRef.current.id === authUser.id) {
        dispatch({ type: authActions.SET_USER, payload: lastUserDataRef.current });
      } else {
        createUserFromAuth(authUser);
      }
    }
  };

  const createUserFromAuth = (authUser) => {
    try {
      const userData = {
        id: authUser.id,
        username: authUser.user_metadata?.full_name ||
          authUser.user_metadata?.name ||
          authUser.email?.split('@')[0] ||
          'user',
        displayName: authUser.user_metadata?.full_name ||
          authUser.user_metadata?.name ||
          'User',
        email: authUser.email,
        avatarUrl: authUser.user_metadata?.avatar_url,
        originalDiscordAvatar: authUser.user_metadata?.avatar_url,
        useDiscordAvatar: true,
        discordId: authUser.user_metadata?.provider_id,
        isAdmin: false,
        bio: '',
        platform: '',
        gamerTag: '',
        position: '',
        jerseyNumber: '',
        stats: {
          tournamentsPlayed: 0,
          tournamentsWon: 0,
          totalWins: 0,
          totalLosses: 0,
          eloRating: 1000
        }
      };

      if (!lastUserDataRef.current) {
        lastUserDataRef.current = userData;
      }
      dispatch({ type: authActions.SET_USER, payload: userData });
    } catch (error) {
      console.error('[AUTH] Failed to create user from auth:', error);
      setError('Authentication failed');
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[AUTH] Session error:', error);
          if (mounted) {
            setIsInitializing(false);
            dispatch({ type: authActions.SET_USER, payload: null });
          }
          return;
        }

        if (session?.user && mounted) {
          await handleUserSession(session.user, false);
        } else if (mounted) {
          dispatch({ type: authActions.SET_USER, payload: null });
        }

        if (mounted) {
          setIsInitializing(false);
        }
      } catch (error) {
        console.error('[AUTH] Auth init error:', error);
        if (mounted) {
          setIsInitializing(false);
          dispatch({ type: authActions.SET_USER, payload: null });
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        switch (event) {
          case 'SIGNED_IN':
            if (!isInitializing && session?.user) {
              const isTokenRefresh = lastUserDataRef.current && lastUserDataRef.current.id === session.user.id;
              await handleUserSession(session.user, isTokenRefresh);
            }
            break;
          case 'SIGNED_OUT':
            lastUserDataRef.current = null;
            dispatch({ type: authActions.LOGOUT });
            break;
          case 'TOKEN_REFRESHED':
            break;
          default:
            break;
        }
      }
    );

    initializeAuth();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
    // handleUserSession is intentionally omitted: it closes over state.user (via syncDiscordProfile),
    // so including it would re-subscribe to auth changes and re-run init on every user update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, dispatch, setError]);

  const signInWithDiscord = async () => {
    return handleAsyncOperation(async () => {
      clearError();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: `${window.location.origin}/dashboard`
        }
      });
      if (error) throw error;
      return { success: true };
    }, errorHandler);
  };

  const signOut = async () => {
    return handleAsyncOperation(async () => {
      lastUserDataRef.current = null;
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch({ type: authActions.LOGOUT });
      return { success: true };
    }, errorHandler);
  };

  // Discord profile sync function
  const syncDiscordProfile = async () => {
    return handleAsyncOperation(async () => {
      if (!state.user) throw new Error('You must be logged in to sync Discord profile');
      
      const { data, error } = await supabase.rpc('sync_discord_profile', {
        user_id_param: state.user.id
      });

      if (error) throw error;

      // Update local state if profile was updated (preserve display name)
      if (data?.success && data?.updated && data?.data) {
        const updatedUser = {
          ...state.user,
          // Don't update displayName - preserve user's custom display name
          avatarUrl: data.data.use_discord_avatar 
            ? (data.data.original_discord_avatar || data.data.avatar_url)
            : data.data.avatar_url,
          originalDiscordAvatar: data.data.original_discord_avatar,
          useDiscordAvatar: data.data.use_discord_avatar
        };

        lastUserDataRef.current = updatedUser;
        dispatch({ type: authActions.SET_USER, payload: updatedUser });
      }

      return { 
        success: data?.success || false, 
        updated: data?.updated || false,
        message: data?.updated ? 'Discord profile synced successfully' : 'Discord profile is already up to date'
      };
    }, errorHandler);
  };

  const updateProfile = async (profileData) => {
    return handleAsyncOperation(async () => {
      if (!state.user) throw new Error('You must be logged in to update your profile');
      
      setLoading(true);
      clearError();

      const updateData = {
        display_name: profileData.displayName,
        bio: profileData.bio,
        platform: profileData.platform,
        gamer_tag: profileData.gamerTag,
        position: profileData.position,
        jersey_number: profileData.jerseyNumber ? parseInt(profileData.jerseyNumber) : null,
        use_discord_avatar: profileData.useDiscordAvatar,
        country_code: profileData.countryCode
      };

      if (profileData.useDiscordAvatar) {
        if (!state.user.originalDiscordAvatar && state.user.avatarUrl) {
          updateData.original_discord_avatar = state.user.avatarUrl;
        }
        updateData.avatar_url = state.user.originalDiscordAvatar || state.user.avatarUrl;
      } else {
        updateData.avatar_url = profileData.avatarUrl;
        if (!state.user.originalDiscordAvatar && state.user.avatarUrl) {
          updateData.original_discord_avatar = state.user.avatarUrl;
        }
      }

      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', state.user.id)
        .select()
        .single();

      if (error) throw error;

      const updatedUser = {
        ...state.user,
        displayName: data.display_name,
        bio: data.bio,
        platform: data.platform,
        gamerTag: data.gamer_tag,
        position: data.position,
        jerseyNumber: data.jersey_number,
        useDiscordAvatar: data.use_discord_avatar,
        avatarUrl: data.use_discord_avatar
          ? (data.original_discord_avatar || data.avatar_url)
          : data.avatar_url,
        originalDiscordAvatar: data.original_discord_avatar,
        countryCode: data.country_code,
        country_code: data.country_code
      };

      lastUserDataRef.current = updatedUser;
      dispatch({ type: authActions.SET_USER, payload: updatedUser });
      setLoading(false);
      
      return { success: true, user: updatedUser };
    }, errorHandler);
  };

  const value = {
    ...state,
    signInWithDiscord,
    signOut,
    logout: signOut,
    updateProfile,
    syncDiscordProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
